import React, { useEffect, useState } from "react";

import { Form, Formik } from "formik";

import AirbyteApi from "apis/airbyte";
import { PageLoader, Typography, Button } from "clearsense-ui/components";
import { Select, Input } from "clearsense-ui/components/formik";
import { useAirbyteState } from "contexts/airbyte";

import {
  DESTINATION_INTIIAL_VALUES,
  DESTINATION_VALIDATION_SCHEMA,
} from "../../constants";
import { getFields } from "../../utils";
import Fields from "../Fields";
import { recursivePayload } from "../utils";

const AddDestination = ({
  setLoading,
  setSelectedDestination,
  setIsPathReady,
  isPathReady,
}) => {
  const [destinationFields, setDestinationFields] = useState([]);
  const [isFieldLoading, setIsFieldLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [destinationDefinitions, setDestinationDefinitions] = useState([]);

  const { workspaceId } = useAirbyteState();

  const fetchDestinationDefinitions = async () => {
    try {
      const { data } = await AirbyteApi.fetchDestinationDefinitions({
        workspaceId,
      });
      const modfiedForSelect = data.destinationDefinitions.map(
        destinationDefinition => ({
          label: destinationDefinition.name,
          value: destinationDefinition.destinationDefinitionId,
        })
      );
      setDestinationDefinitions(
        modfiedForSelect.sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDestinationDefinitions();
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  const handleSubmit = async ({ destination, type, ...otherParameters }) => {
    const connectionConfiguration = recursivePayload(otherParameters);
    const payload = {
      name: destination,
      destinationDefinitionId: type?.value,
      workspaceId,
      connectionConfiguration,
    };
    try {
      setLoading(true);
      const response = await AirbyteApi.createDestination(payload);
      setSelectedDestination(response.data.destinationId);
      setIsPathReady(true);
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (resetForm, values, option) => {
    resetForm({ values: { destination: values.destination, type: option } });
    const payload = { destinationDefinitionId: option.value };
    setIsFieldLoading(true);
    try {
      const {
        data: { connectionSpecification },
      } = await AirbyteApi.fetchDestinationDefinitionSpecifications(payload);
      setDestinationFields(getFields(connectionSpecification));
    } catch (error) {
      logger.error(error);
    } finally {
      setIsFieldLoading(false);
    }
  };

  return (
    <Formik
      initialValues={DESTINATION_INTIIAL_VALUES}
      validationSchema={DESTINATION_VALIDATION_SCHEMA}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, resetForm, values }) => (
        <Form className="space-y-4">
          <Typography style="h4" className="mt-6 border-b-2">
            Create New Target
          </Typography>
          <Input
            label="Name"
            name="destination"
            placeholder="Enter a name for the target"
            data-cy="new-target-name-input"
          />
          <Select
            label="Type"
            required
            name="type"
            placeholder="Select a target"
            options={destinationDefinitions}
            className="w-3/4 mt-4"
            onChange={option => handleChange(resetForm, values, option)}
            data-cy="new-target-type-select"
          />
          {isFieldLoading && (
            <>
              <Typography style="h5" className="mb-4 text-center">
                Loading required parameters
              </Typography>
              <PageLoader />
            </>
          )}
          {destinationFields.length > 0 && (
            <>
              <Fields
                fields={destinationFields}
                setFieldValue={setFieldValue}
              />
              <Button
                type="submit"
                label="Create Target"
                disabled={isPathReady}
                data-cy="new-target-submit-button"
              />
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default AddDestination;
